import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "@dolfin/api-admin";
import { SESSION_STORAGE_KEY } from "@/lib/constants";
import { notifyForbiddenFromResponse } from "@/lib/api-error";
import { isTokenDue } from "@dolfin/lib";
import { sessionRefresher } from "@/auth/session";

interface StoredSession {
  token: string;
  activeOrganisationId: string;
}

function getSession(): StoredSession | null {
  try {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return { token: parsed.token, activeOrganisationId: parsed.activeOrganisationId };
  } catch {
    return null;
  }
}

// The refresh cookie is dead, so the session is over: back to sign-in.
function handle401() {
  localStorage.removeItem(SESSION_STORAGE_KEY);
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

const loggingMiddleware: Middleware = {
  async onRequest({ request }) {
    console.groupCollapsed(`→ ${request.method} ${request.url}`);
    const headers: Record<string, string> = {};
    request.headers.forEach((value, key) => {
      headers[key] = value;
    });
    console.log("headers", headers);
    console.groupEnd();
    return request;
  },
  async onResponse({ response }) {
    const body = await response.clone().json().catch(() => null);
    console.groupCollapsed(`← ${response.status} ${response.url}`);
    console.log(body);
    console.groupEnd();
    return response;
  },
};

// Unsent copies of in-flight requests, by openapi-fetch request id, so a 401 can be replayed with a fresh token
// (a Request body can only be sent once).
const replayable = new Map<string, Request>();

const authMiddleware: Middleware = {
  async onRequest({ request, id }) {
    let session = getSession();
    if (session?.token && isTokenDue(session.token)) {
      await sessionRefresher.refresh(session.token);
      session = getSession();
    }
    if (session?.token) {
      request.headers.set("Authorization", `Bearer ${session.token}`);
    }
    if (session?.activeOrganisationId) {
      request.headers.set("x-dolfin-organisation-id", session.activeOrganisationId);
    }
    replayable.set(id, request.clone());
    return request;
  },
  async onResponse({ request, response, id }) {
    const copy = replayable.get(id);
    replayable.delete(id);
    if (response.status === 401) {
      const sent = request.headers.get("Authorization")?.replace(/^Bearer /, "") ?? null;
      const outcome = sent ? await sessionRefresher.refresh(sent) : { kind: "ended" as const };
      if (outcome.kind === "refreshed" && copy) {
        copy.headers.set("Authorization", `Bearer ${outcome.token}`);
        return fetch(copy);
      }
      if (outcome.kind === "ended") handle401();
      return response;
    }
    // Entitlement 403s are surfaced here rather than per call site: a module the
    // client has not bought fails every request on the page, reads included.
    void notifyForbiddenFromResponse(response);
    return response;
  },
  onError({ id }) {
    replayable.delete(id);
  },
};

export const api = createClient<paths>({
  baseUrl: "/api/proxy",
});

api.use(authMiddleware);

if (process.env.NODE_ENV !== "production") {
  api.use(loggingMiddleware);
}

/** For endpoints the openapi client doesn't cover: adds the token and org header, and handles 401. */
export async function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await sessionRefresher.fetchWithRefresh(input, init, (headers, token) => {
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const orgId = getSession()?.activeOrganisationId;
    if (orgId) headers.set("x-dolfin-organisation-id", orgId);
  });

  // Still 401 after the refresh attempt, and the session has been cleared: it's over.
  if (response.status === 401 && !getSession()) {
    handle401();
  }
  void notifyForbiddenFromResponse(response);

  return response;
}
